import {
    Button,
    Container,
    Flex,
    rem,
    TextInput,
    Modal,
    Card,
    Title,
    Image,
    Box,
    Pagination,
    Text,
    Grid,
    Loader,
} from '@mantine/core';
import { HiOutlineWrenchScrewdriver } from "react-icons/hi2";
import { IconPlant } from '@tabler/icons-react';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { OrganizationForm } from '../../../features/organization/ui/organizationForm';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../../utils/appRoutes';
import { receiveVisibleFpfs } from '../../../features/fpf/useCase/receiveVisibleFpfs';
import { BasicFPF } from '../../../features/fpf/models/BasicFPF';
import { useTranslation } from 'react-i18next';
import Footer from '../footer/footer';
import { useMediaQuery } from '@mantine/hooks';
import {showNotification} from "@mantine/notifications";

const LandingPage: React.FC<PropsWithChildren<{}>> = () => {
    const auth = useAuth();
   // const [organizations, setOrganizations] = useState<OrganizationMembership[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    /*const organizationEventListener = useSelector(
        (state: RootState) => state.organization.createdOrganizationEvent
    ); */
    const navigate = useNavigate();
    const [fpfs, setFpfs] = useState<BasicFPF[]>([]);
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const ITEMS_PER_PAGE = 6;
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    // Detect mobile screens
    const isMobile = useMediaQuery('(max-width: 768px)');

    // Filter fpfs based on search term
    const filteredFpfs = fpfs?.filter(
        (fpf) =>
            fpf.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            fpf.organization.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Paginate the filtered fpfs list
    const paginatedFpfs = filteredFpfs?.slice(startIndex, endIndex);

    //Redux Store
    //const dispatch = useAppDispatch();
   // const myOrganizationsSelector = useAppSelector((state) => state.organization.myOrganizations);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    useEffect(() => {
        const fetchFpfs = () => {
            setLoading(true);
            receiveVisibleFpfs()
                .then((r) => setFpfs(r))
                .catch((error) => {
                    showNotification({
                        title: t('common.loadError'),
                        message: `${error}`,
                        color: 'red',
                    });
                })
                .finally(() => setLoading(false));
        };

        // Initial fetch
        fetchFpfs();

        // Poll every 5 minutes
        const interval = setInterval(fetchFpfs, 300000);

        return () => clearInterval(interval);
    }, [t]);

    /* not needed
    useEffect(() => {
        if (auth.isAuthenticated) {
            setOrganizations(myOrganizationsSelector);
        }
    }, [auth.isAuthenticated, organizationEventListener, t, myOrganizationsSelector]);
    */

    const handleFpfSelect = (organizationId: string, fpfId: string) => {
        navigate(
            AppRoutes.displayFpf
                .replace(':organizationId', organizationId)
                .replace(':fpfId', fpfId)
        );
    };

    return (
        // Outer container with a minimum height; the entire page scrolls naturally if content overflows
        <Box style={{ display: 'flex', flexDirection: 'column', minHeight: '92vh', overflow:'hidden' }}>
            {/* Header / Search Section */}
            <Box>
                <Container size={isMobile ? 'xs' : 'lg'} py={isMobile ? 'md' : 'xl'}>
                    <Flex direction={isMobile ? 'column' : 'row'} align="center" justify="center" gap="md">
                        <TextInput
                            placeholder={t('header.search')}
                            value={searchTerm}
                            onChange={handleSearchChange}
                            style={{ width: isMobile ? '90%' : '30vw' }}
                        />
                        {auth.isAuthenticated && (
                            <Button
                                onClick={() => setModalOpen(true)}
                                variant="filled"
                                color="#199ff4"
                                style={{ width: isMobile ? '90%' : 'auto' }}
                            >
                                {t('header.createOrganization')}
                            </Button>
                        )}
                    </Flex>
                </Container>
            </Box>

            {/* FPFS List */}
            <Box style={{ flex: 1, overflow:'hidden' }}>
                <Container size={isMobile ? 'xs' : 'lg'} style ={{overflowX:'hidden'}}>
                    {loading ? (
                        <Flex justify="center" align="center" style={{ height: '50vh' }}>
                            <Loader size="lg" />
                        </Flex>
                    ) : (
                        <>
                            <Grid gutter={isMobile ? 'sm' : 'md'}>
                                {paginatedFpfs &&
                                    paginatedFpfs.map((fpf) => (
                                        <Grid.Col span={isMobile ? 12 : 4} key={fpf.id}>
                                            <Card
                                                p="lg"
                                                radius="md"
                                                style={{
                                                    height: '25vh',
                                                    margin: isMobile ? '10px 0' : '15px',
                                                    cursor: 'pointer',
                                                    transition: 'transform 0.2s ease',
                                                }}
                                                onClick={() => handleFpfSelect(fpf.organization.id, fpf.id)}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'scale(1.02)';
                                                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.2)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'scale(1)';
                                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
                                                }}
                                            >
                                                {!fpf.isActive && (
                                                    <Box
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: '16px',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            borderRadius: 'inherit',
                                                            zIndex: 1,
                                                            backdropFilter: 'blur(1px)'
                                                        }}
                                                    >

                                                        <HiOutlineWrenchScrewdriver size={64} color="white" />
                                                        <Text c="white" fw={700} size="xl">

                                                            {t('common.maintenance')}

                                                        </Text>
                                                    </Box>
                                                )}
                                                <Flex justify="space-between" align="center" mb="sm">
                                                    <Title
                                                        order={3}
                                                        style={{
                                                            fontSize: '1.1rem',
                                                            fontWeight: 600,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {fpf.name}
                                                    </Title>
                                                    <Text style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                                        {fpf.organization.name}
                                                    </Text>
                                                </Flex>
                                                <Box
                                                    style={{
                                                        height: '80%',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    {fpf.lastImageUrl?.length ? (
                                                        <Image
                                                            src={fpf.lastImageUrl}
                                                            alt="Last Received Image"
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                borderRadius: '5px',
                                                            }}
                                                        />
                                                    ) : (
                                                        <IconPlant size={rem(40)} color="gray" style={{ opacity: 0.5 }} />
                                                    )}
                                                </Box>
                                            </Card>
                                        </Grid.Col>
                                    ))}
                            </Grid>
                            <Flex justify="center" mt="lg" style={{overflow:'hidden'}}>
                                {filteredFpfs && (
                                    <Pagination
                                        total={Math.ceil(filteredFpfs.length / ITEMS_PER_PAGE) || 1}
                                        siblings={isMobile ? 1 : 2}
                                        defaultValue={currentPage}
                                        onChange={handlePageChange}
                                    />
                                )}
                            </Flex>
                        </>
                    )}
                </Container>
            </Box>

            {/* Footer */}
            <Footer />

            {/* Modal for Creating Organization */}
            <Modal
                opened={modalOpen}
                onClose={() => setModalOpen(false)}
                title={t('header.addOrganization')}
                centered
            >
                <OrganizationForm />
            </Modal>
        </Box>
    );
};

export default LandingPage;
